import { Box, Checkbox, Skeleton, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { TreeNode } from '../Types/tree';

interface CheckboxTreeProps<T> {
  nodes: TreeNode<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  getNodeValue: (node: TreeNode<T>) => T | null | undefined;
  loading?: boolean;
}

function CheckboxTreeSkeleton() {
  return (
    <Box>
      {Array.from({ length: 5 }).map((_, parentIndex) => (
        <Box key={parentIndex} sx={{ mb: 1 }}>
          {/* Parent row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="rectangular" width={18} height={18} />
            <Skeleton variant="text" width={400} height={30} />
          </Box>

          {/* Child rows (random-ish count for realism) */}
          <Box sx={{ pl: 4, mt: 0.5 }}>
            {Array.from({ length: 2 + (parentIndex % 4) }).map((_, childIndex) => (
              <Box key={childIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Skeleton variant="rectangular" width={18} height={18} />
                <Skeleton variant="text" width={600} height={30} />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function CheckboxTree<T>({
  nodes,
  value,
  onChange,
  getNodeValue,
  loading,
}: CheckboxTreeProps<T>) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const collectLeafNodes = useCallback((node: TreeNode<T>): TreeNode<T>[] => {
    if (!node.children?.length) return [node];
    return node.children.flatMap(collectLeafNodes);
  }, []);

  // Cache all leaf nodes
  const leafNodes = useMemo(() => nodes.flatMap(collectLeafNodes), [nodes, collectLeafNodes]);

  // Sync external value → internal IDs
  useEffect(() => {
    const ids = value
      .map((v) => leafNodes.find((n) => n.data === v)?.id)
      .filter(Boolean) as string[];

    setSelectedIds(ids);
  }, [value, leafNodes]);

  const emitChange = useCallback(
    (ids: string[]) => {
      const selectedValues = leafNodes
        .filter((n) => ids.includes(n.id))
        .map((n) => getNodeValue(n))
        .filter(Boolean) as T[];

      onChange(selectedValues);
    },
    [leafNodes, getNodeValue, onChange]
  );

  const toggleNode = useCallback(
    (node: TreeNode<T>) => {
      const leafIds = collectLeafNodes(node).map((n) => n.id);
      const allSelected = leafIds.every((id) => selectedIds.includes(id));

      const nextIds = allSelected
        ? selectedIds.filter((id) => !leafIds.includes(id))
        : Array.from(new Set([...selectedIds, ...leafIds]));

      setSelectedIds(nextIds);
      emitChange(nextIds);
    },
    [collectLeafNodes, emitChange, selectedIds]
  );

  const getCheckboxState = (node: TreeNode<T>) => {
    const leafIds = collectLeafNodes(node).map((n) => n.id);
    const count = leafIds.filter((id) => selectedIds.includes(id)).length;

    return {
      checked: count === leafIds.length,
      indeterminate: count > 0 && count < leafIds.length,
    };
  };

  const renderNode = (node: TreeNode<T>) => {
    const { checked, indeterminate } = getCheckboxState(node);

    return (
      <TreeItem
        key={node.id}
        itemId={node.id}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              size="small"
              checked={checked}
              indeterminate={indeterminate}
              onChange={() => toggleNode(node)}
              onClick={(e) => e.stopPropagation()}
            />
            <Typography variant="body2">{node.label}</Typography>
          </Box>
        }
      >
        {node.children?.map(renderNode)}
      </TreeItem>
    );
  };

  if (loading) {
    return <CheckboxTreeSkeleton />;
  }

  return <SimpleTreeView>{nodes.map(renderNode)}</SimpleTreeView>;
}
